import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  X, 
  Shield, 
  Lock, 
  Mail, 
  Loader2,
  AlertCircle,
  CheckCircle
} from 'lucide-react';
import api from '@/services/api';
import { useAuth } from '@/context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { setManagedToken } from '../lib/authSession';
import { MfaChallenge } from './security/MfaChallenge';

const AdminLoginModal = ({ isOpen, onClose }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const { setToken, setUser, refreshUser } = useAuth();
  const [challenge, setChallenge] = useState(null);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess(false);
    setLoading(true);

    try {
      const response = await api.post('/admin/login', { email, password });
      if (response.data.mfa_required) { setChallenge(response.data); return; }
      
      if (response.data.access_token) {
        // Clear any previous error and set success
        setError('');
        setSuccess(true);
        // Use the correct localStorage key
        localStorage.setItem('notary_token', response.data.access_token);
        localStorage.removeItem('notary_auth_method');
        setManagedToken(null);
        setToken(response.data.access_token);
        setUser(response.data.user);
        await refreshUser();
        
        setTimeout(() => {
          onClose();
          // Use window.location for full page reload to ensure context updates
          window.location.href = '/admin';
        }, 1000);
      }
    } catch (err) {
      // Clear success state and set error
      setSuccess(false);
      const message = err.response?.data?.detail || 'Error al iniciar sesión';
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  if (!isOpen) return null;
  if (challenge) return <div data-testid="admin-mfa-dialog" className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/70 p-4"><div className="w-full max-w-md rounded-xl border border-slate-700 bg-slate-900 p-5"><MfaChallenge challenge={challenge} onCancel={() => setChallenge(null)} onDone={() => { setChallenge(null); onClose(); navigate('/admin'); }} /></div></div>;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={handleBackdropClick}
        className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
        data-testid="admin-login-modal"
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0, y: 20 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.95, opacity: 0, y: 20 }}
          transition={{ duration: 0.2 }}
          className="w-full max-w-md bg-slate-900 rounded-2xl shadow-2xl border border-slate-700 overflow-hidden"
        >
          {/* Header */}
          <div className="relative bg-gradient-to-r from-red-600 to-orange-500 p-6">
            <button
              onClick={onClose}
              data-testid="admin-login-close"
              aria-label="Cerrar acceso de administrador"
              className="absolute top-4 right-4 text-white/70 hover:text-white transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                <Shield className="w-7 h-7 text-white" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-white">Panel de Administración</h2>
                <p className="text-red-100 text-sm">Acceso restringido</p>
              </div>
            </div>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="p-6 space-y-5">
            {/* Error Message - Only show if no success */}
            {error && !success && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="flex items-center space-x-2 p-3 bg-red-500/10 border border-red-500/20 rounded-lg text-red-400 text-sm"
              >
                <AlertCircle className="w-4 h-4 flex-shrink-0" />
                <span>{error}</span>
              </motion.div>
            )}

            {/* Success Message - Only show on success */}
            {success && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="flex items-center space-x-2 p-3 bg-green-500/10 border border-green-500/20 rounded-lg text-green-400 text-sm"
              >
                <CheckCircle className="w-4 h-4 flex-shrink-0" />
                <span>Acceso concedido. Redirigiendo...</span>
              </motion.div>
            )}

            {/* Email */}
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">
                Correo del administrador
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  disabled={loading || success}
                  placeholder="admin@ejemplo.com"
                  className="w-full pl-11 pr-4 py-3 bg-slate-800 border border-slate-700 rounded-lg focus:border-red-500 focus:ring-1 focus:ring-red-500 transition-all text-white placeholder-slate-500 disabled:opacity-50"
                  data-testid="admin-email-input"
                />
              </div>
            </div>

            {/* Password */}
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">
                Contraseña
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  disabled={loading || success}
                  placeholder="••••••••"
                  className="w-full pl-11 pr-4 py-3 bg-slate-800 border border-slate-700 rounded-lg focus:border-red-500 focus:ring-1 focus:ring-red-500 transition-all text-white placeholder-slate-500 disabled:opacity-50"
                  data-testid="admin-password-input"
                />
              </div>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={loading || success}
              className="w-full py-3 bg-gradient-to-r from-red-600 to-orange-500 hover:from-red-500 hover:to-orange-400 text-white font-semibold rounded-lg transition-all duration-200 flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="admin-login-submit"
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  <span>Verificando...</span>
                </>
              ) : success ? (
                <>
                  <CheckCircle className="w-5 h-5" />
                  <span>Acceso concedido</span>
                </>
              ) : (
                <>
                  <Shield className="w-5 h-5" />
                  <span>Acceder al Panel</span>
                </>
              )}
            </button>

            {/* Notice */}
            <p className="text-center text-slate-500 text-xs">
              Este acceso está reservado exclusivamente para administradores autorizados.
              Todos los intentos de acceso son registrados.
            </p>
          </form>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default AdminLoginModal;
